import { WeightValueEntry, WeightValues } from "@/types/dataset";
import { parseWeightValue, WeightPrimitive } from "@/utils/weightValues";

export const calculatePercentagesWithRounding = <T>(
  entries: [string, T][],
  getWeight: (value: T) => number,
  totalWeight: number,
  targetSum: number
): Record<string, string> => {
  if (totalWeight === 0) {
    return Object.fromEntries(entries.map(([id]) => [id, "0.00"]));
  }

  const result: Record<string, string> = {};
  let sum = 0;

  entries.forEach(([id, value], i) => {
    const pct = (getWeight(value) / totalWeight) * targetSum;
    const val =
      i === entries.length - 1 ? targetSum - sum : parseFloat(pct.toFixed(2));
    result[id] = val.toFixed(2);
    sum += val;
  });

  return result;
};

export const initializeDatasetWeights = (
  weightValues: WeightValues
): WeightValues => {
  return Object.fromEntries(
    Object.entries(weightValues).map(([datasetId, dataset]) => [
      datasetId,
      {
        weight: dataset.weight,
        attributes: dataset.attributes
      }
    ])
  );
};

export const calculateAttributeGlobalPercentages = (
  datasetAttributesWeights: Record<string, WeightValueEntry>,
  datasetWeightPercentage: string
): Record<string, string> => {
  const totalLocalAttributeWeight = Object.values(
    datasetAttributesWeights
  ).reduce((sum, weight) => sum + parseWeightValue(weight), 0);

  return calculatePercentagesWithRounding(
    Object.entries(datasetAttributesWeights),
    parseWeightValue,
    totalLocalAttributeWeight,
    parseWeightValue(datasetWeightPercentage)
  );
};

export const calculateDatasetWeightPercentages = (
  weightValues: WeightValues
): Record<string, string> => {
  const totalWeight = Object.values(weightValues).reduce(
    (sum, dataset) => sum + parseWeightValue(dataset.weight),
    0
  );

  return calculatePercentagesWithRounding(
    Object.entries(weightValues),
    (dataset) => parseWeightValue(dataset.weight),
    totalWeight,
    100
  );
};

export const calculateWeightPercentage = (
  attributeId: string,
  datasetTotalWeight: number,
  datasetAttributeWeights: Record<string, WeightPrimitive>
): string => {
  if (!datasetTotalWeight || datasetTotalWeight <= 0) {
    return "0%";
  }

  const attributeWeight = datasetAttributeWeights?.[attributeId];
  const parsedWeight = parseWeightValue(attributeWeight);

  if (parsedWeight <= 0) {
    return "0%";
  }

  const percentage = (parsedWeight / datasetTotalWeight) * 100;
  const formattedPercentage = percentage
    .toFixed(2)
    .replace(/\.00$/, "")
    .replace(/(\.\d)0$/, "$1");

  return `${formattedPercentage}%`;
};
